import type { PropsWithChildren } from 'react'
import { createContext, useMemo } from 'react'
import type { IntlShape } from 'react-intl'
import { createIntl, createIntlCache, IntlProvider, useIntl } from 'react-intl'

import { registerYupLocale } from '../i18n/registerYupLocale'

export type YupErrorMapProviderProps = PropsWithChildren<{ intl?: IntlShape }>

const YupErrorMapContext = createContext<YupErrorMapProviderProps>({})

let intlCache: ReturnType<typeof createIntlCache> | undefined

/**
 * Cache shared by every `createIntl` call in this module.
 *
 * Built on first render rather than at module scope so importing this file never touches
 * `react-intl`, which keeps consumers that stub the module with partial factories working.
 */
const getIntlCache = () => (intlCache ??= createIntlCache())

/**
 * Provider that sets the Yup locale via FormatJS.
 *
 * Returns a nested `IntlProvider` with the messages from the parent `IntlProvider`.
 *
 * !!!: Relies on `IntlProvider` from `react-intl` being present in the component tree.
 *
 * @see {@link https://github.com/formatjs/formatjs/issues/1109}
 */
export const YupErrorMapProvider = ({ intl: intlOverride, children }: YupErrorMapProviderProps) => {
  const parentIntl = useIntl()
  const intl = intlOverride ?? parentIntl

  const parentMessages: Record<string, string> = useMemo(
    () => (intl?.messages as Record<string, string>) ?? {},
    [intl?.messages]
  )

  const { formatMessage } = useMemo(
    () => createIntl({ locale: intl?.locale ?? 'en', messages: parentMessages }, getIntlCache()),
    [intl?.locale, parentMessages]
  )

  // A memo rather than an effect: descendants build schemas while rendering, and Yup
  // captures a schema's message at construction time.
  useMemo(() => registerYupLocale(formatMessage), [formatMessage])

  return (
    <IntlProvider locale={intl?.locale ?? 'en'} messages={parentMessages}>
      <YupErrorMapContext.Provider value={{ intl }}>{children}</YupErrorMapContext.Provider>
    </IntlProvider>
  )
}
